import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { TabView, TabPanel } from 'primereact/tabview';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { useWeb3React } from "@web3-react/core";
import Web3 from "web3";
import ContractContext from '../../ContractContext';
import { Toast } from "primereact/toast";
import { Card as NextCard } from "@nextui-org/react";
import { RadioButton } from 'primereact/radiobutton';
import TokenSelectorModal from './TokenSelectorModal';
import TokenInput from './TokenInput';
import { getTokenLogo, formatBalanceWithIcon } from '../../utils';
import ierc20Abi from "../../contracts/ierc20Abi";

const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
    padding: '0 10px',
};

const miniCardStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignContent: 'start',
    width: '100%',
    padding: '12px',
    backgroundColor: 'white',
    outline: '1px solid grey',
    borderRadius: '15px',
    marginTop: '10px',
    marginBottom: '10px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
};

const FloatieDAO = React.memo(({ tokenSelections, refreshData, imageReferences }) => {
    const { account, library } = useWeb3React();
    const contractContext = useContext(ContractContext);
    const contracts = contractContext.contracts;
    const [selectedToken, setSelectedToken] = useState(null);
    const [tokenBalance, setTokenBalance] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [filteredTokens, setFilteredTokens] = useState([]);
    const [question, setQuestion] = useState('');
    const [answers, setAnswers] = useState(['']);
    const [duration, setDuration] = useState(null);
    const [activeProposals, setActiveProposals] = useState([]);
    const [pendingProposals, setPendingProposals] = useState([]);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [votedProposals, setVotedProposals] = useState([]);
    const toast = useRef(null);

    const fetchProposals = useCallback(async () => {
        if (!contracts || !contracts.votingProposal || !selectedToken) return;

        const votingContract = contracts.votingProposal;
        const active = [];
        const voted = [];
        const pending = [];

        try {
            const nextProposalId = await votingContract.methods.nextProposalId(selectedToken.address).call();
            for (let i = 0; i < nextProposalId; i++) {
                const proposal = await votingContract.methods.proposals(selectedToken.address, i).call();
                if (proposal.exists && !proposal.closed) {
                    const answers = await votingContract.methods.getProposal(selectedToken.address, proposal.id).call();
                    proposal.answers = answers[1]; // Assuming answers are returned in the second position of the array
                    proposal.voteCounts = answers[2]; // Assuming vote counts are returned in the third position of the array
                    proposal.isOpen = answers[3]; // Assuming isOpen flag is returned in the fourth position of the array
                    const hasVoted = await votingContract.methods.hasVoted(selectedToken.address, account, proposal.id).call();
                    if (proposal.active) {
                        if (hasVoted) {
                            voted.push({ token: selectedToken, proposal });
                        } else {
                            active.push({ token: selectedToken, proposal });
                        }
                    }
                }
            }

            const nextPendingProposalId = await votingContract.methods.nextPendingProposalId(selectedToken.address).call();
            for (let i = 0; i < nextPendingProposalId; i++) {
                const pendingProposal = await votingContract.methods.pendingProposals(selectedToken.address, i).call();
                const isActive = active.some(ap => ap.proposal.id === pendingProposal.id);
                const isVoted = voted.some(vp => vp.proposal.id === pendingProposal.id);
                if (pendingProposal.exists && !isActive && !isVoted && !pendingProposal.closed) {
                    pending.push({ token: selectedToken, pendingProposal });
                }
            }

            setActiveProposals(active);
            setVotedProposals(voted);
            setPendingProposals(pending);
        } catch (error) {
            console.error('Error fetching proposals:', error);
        }
    }, [contracts, selectedToken, account]);

    useEffect(() => {
        fetchProposals();
    }, [fetchProposals]);

    useEffect(() => {
        if (selectedToken && selectedToken.address) {
            getTokenBalance(selectedToken).then(balance => setTokenBalance(balance));
        } else {
            setTokenBalance(0);
        }
    }, [selectedToken, account, library]);

    useEffect(() => {
        if (!tokenSelections) return;
        const storedToken = localStorage.getItem('selectedTokenFloatieDAO');
        const foundToken = tokenSelections.find(token => token.address === storedToken);

        if (foundToken) {
            setSelectedToken(foundToken);
        } else {
            setSelectedToken(null);
        }
    }, [tokenSelections]);

    const getTokenLogoUrl = (token) => {
        return getTokenLogo(imageReferences, token);
    };

    const getTokenBalance = useCallback(async (token) => {
        try {
            if (!library?.provider || !token) return 0;
            const web3 = new Web3(library.provider);
            if (token.label === 'WBNB') {
                const balance = await web3.eth.getBalance(account);
                return balance;
            } else {
                const contract = new web3.eth.Contract(ierc20Abi, token.address);
                const balance = await contract.methods.balanceOf(account).call();
                return balance;
            }
        } catch (error) {
            console.error("Error fetching token balance:", error);
            return 0;
        }
    }, [account, library]);

    const handleSubmitProposal = async () => {
        if (!question || answers.length < 2 || !contracts || !contracts.votingProposal || !selectedToken) return;

        const votingContract = contracts.votingProposal;

        try {
            await votingContract.methods.submitProposal(selectedToken.address, question, answers, duration ? Math.floor(duration.getTime() / 1000) : 0).send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Proposal Submitted', detail: 'Your proposal has been submitted for review.', life: 3000 });
            setQuestion('');
            setAnswers(['']);
            setDuration(null);
            fetchProposals();
        } catch (error) {
            console.error("Error submitting proposal:", error);
            toast.current.show({ severity: 'error', summary: 'Submission Error', detail: error.toString(), life: 5000 });
        }
    };

    const handleActivateProposal = async (token, proposalId) => {
        if (!contracts || !contracts.votingProposal) return;

        const votingContract = contracts.votingProposal;

        try {
            await votingContract.methods.activateProposal(token.address, proposalId).send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Proposal Activated', detail: 'The proposal has been activated.', life: 3000 });
            await fetchProposals();  // Ensure proposals are re-fetched and state is updated
        } catch (error) {
            console.error("Error activating proposal:", error);
            toast.current.show({ severity: 'error', summary: 'Activation Error', detail: error.toString(), life: 5000 });
        }
    };

    const handleVote = async (token, proposalId, answerId, amount) => {
        if (!contracts || !contracts.votingProposal) return;

        const votingContract = contracts.votingProposal;
        try {
            await votingContract.methods.vote(token.address, proposalId, answerId, amount).send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Vote Cast', detail: 'Your vote has been cast.', life: 3000 });
            setSelectedAnswers(prevState => ({ ...prevState, [proposalId]: null }));
            fetchProposals();
        } catch (error) {
            console.error("Error casting vote:", error);
            toast.current.show({ severity: 'error', summary: 'Voting Error', detail: error.toString(), life: 5000 });
        }
    };

    const handleTokenClick = useCallback(() => {
        setShowModal(true);
        setFilteredTokens(tokenSelections.filter(t => t.address.toLowerCase() !== selectedToken?.address.toLowerCase()));
    }, [selectedToken, tokenSelections]);

    const handleTokenSelect = useCallback((token) => {
        setSelectedToken(token);
        localStorage.setItem('selectedTokenFloatieDAO', token.address);
        setShowModal(false);
    }, []);

    const handleCloseModal = useCallback(() => {
        setShowModal(false);
    }, []);

    const handleAnswerSelect = (proposalId, answerId) => {
        setSelectedAnswers(prevState => ({ ...prevState, [proposalId]: answerId }));
    };


    return (
        <div style={{ ...containerStyle, marginTop: '20px' }}>
            <div style={containerStyle}>
                <TokenInput
                    onSelectToken={handleTokenClick}
                    token={selectedToken}
                    balance={tokenBalance}
                    getTokenLogoUrl={getTokenLogoUrl}
                />
                {
                    (!activeProposals?.length && !votedProposals?.length && !pendingProposals?.length) &&
                    <div style={miniCardStyle}>
                        <div style={{ marginBottom: '10px' }}>
                            <label htmlFor="question">Question:</label>
                            <InputText id="question" value={question} onChange={(e) => setQuestion(e.target.value)} style={{ width: '100%' }} />
                        </div>
                        {answers?.map((answer, index) => (
                            <div key={index} style={{ marginBottom: '10px' }}>
                                <label htmlFor={`answer-${index}`}>Answer {index + 1}:</label>
                                <InputText id={`answer-${index}`} value={answer} onChange={(e) => {
                                    const newAnswers = [...answers];
                                    newAnswers[index] = e.target.value;
                                    setAnswers(newAnswers);
                                }} style={{ width: '100%' }} />
                            </div>
                        ))}
                        <Button label="Add Answer" onClick={() => setAnswers([...answers, ''])} />
                        <div style={{ marginTop: '10px' }}>
                            <label htmlFor="duration">Duration (optional):</label>
                            <Calendar id="duration" value={duration} onChange={(e) => setDuration(e.value)} showTime showSeconds placeholder="Select a Date & Time" minDate={new Date()} hourFormat="12" showIcon showButtonBar />
                        </div>
                        <Button label="Submit Proposal" onClick={handleSubmitProposal} style={{ marginTop: '20px' }} />
                    </div>
                }
                {(activeProposals?.length > 0 || votedProposals?.length > 0 || pendingProposals?.length > 0) && <TabView
                    activeIndex={1}
                >


                    <TabPanel header="View Active Proposals (Not Voted)">
                        {activeProposals?.map(({ token, proposal }) => (
                            <NextCard key={proposal.id} style={miniCardStyle}>
                                <NextCard.Body style={{ padding: '3px', overflow: 'auto' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{proposal.question}</div>
                                        {proposal?.answers?.map((answer, index) => (
                                            <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
                                                <RadioButton inputId={`answer-${proposal.id}-${index}`} name={`answer-${proposal.id}`} value={index} onChange={(e) => handleAnswerSelect(proposal.id, index)} checked={selectedAnswers[proposal.id] === index} />
                                                <label htmlFor={`answer-${proposal.id}-${index}`} style={{ marginLeft: '5px' }}>{answer}</label>
                                            </div>
                                        ))}
                                        <Button label="Vote" onClick={() => handleVote(token, proposal.id, selectedAnswers[proposal.id], 1)} disabled={selectedAnswers[proposal.id] === null} />
                                    </div>
                                </NextCard.Body>
                            </NextCard>
                        ))}
                    </TabPanel>



                    <TabPanel header="View Voted Proposals">
                        {votedProposals?.map(({ token, proposal }) => (
                            <NextCard key={proposal.id} style={miniCardStyle}>
                                <NextCard.Body style={{ padding: '3px', overflow: 'auto' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{proposal.question}</div>
                                        <div>Total Votes: {proposal.totalVotes}</div>
                                        {proposal?.answers?.map((answer, index) => (
                                            <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
                                                <label style={{ marginLeft: '5px' }}>{answer} - Votes: {proposal.voteCounts[index]}</label>
                                            </div>
                                        ))}
                                    </div>
                                </NextCard.Body>
                            </NextCard>
                        ))}
                    </TabPanel>

                    <TabPanel header="Submit a Proposal">
                        <div style={miniCardStyle}>
                            <div style={{ marginBottom: '10px' }}>
                                <label htmlFor="question">Question:</label>
                                <InputText id="question" value={question} onChange={(e) => setQuestion(e.target.value)} style={{ width: '100%' }} />
                            </div>
                            {answers?.map((answer, index) => (
                                <div key={index} style={{ marginBottom: '10px' }}>
                                    <label htmlFor={`answer-${index}`}>Answer {index + 1}:</label>
                                    <InputText id={`answer-${index}`} value={answer} onChange={(e) => {
                                        const newAnswers = [...answers];
                                        newAnswers[index] = e.target.value;
                                        setAnswers(newAnswers);
                                    }} style={{ width: '100%' }} />
                                </div>
                            ))}
                            <Button label="Add Answer" onClick={() => setAnswers([...answers, ''])} />
                            <div style={{ marginTop: '10px' }}>
                                <label htmlFor="duration">Duration (optional):</label>
                                <Calendar id="duration" value={duration} onChange={(e) => setDuration(e.value)} showTime showSeconds placeholder="Select a Date & Time" minDate={new Date()} hourFormat="12" showIcon showButtonBar />
                            </div>
                            <Button label="Submit Proposal" onClick={handleSubmitProposal} style={{ marginTop: '20px' }} />
                        </div>
                    </TabPanel>


                    <TabPanel header="Approve Proposals (Admin)">
                        {pendingProposals?.map(({ token, pendingProposal }) => (
                            <NextCard key={pendingProposal?.id} style={miniCardStyle}>
                                <NextCard.Body style={{ padding: '3px', overflow: 'auto' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{pendingProposal?.question}</div>
                                        {pendingProposal?.answers?.map((answer, index) => (
                                            <div key={index}>{answer}</div>
                                        ))}
                                        <Button label="Activate Proposal" onClick={() => handleActivateProposal(token, pendingProposal.id)} />
                                    </div>
                                </NextCard.Body>
                            </NextCard>
                        ))
                        }
                    </TabPanel>
                </TabView>}
            </div>
            {showModal && (
                <TokenSelectorModal
                    tokens={filteredTokens}
                    onSelect={handleTokenSelect}
                    onClose={handleCloseModal}
                    isVisible={showModal}
                    setShowModal={setShowModal}
                    getTokenLogoUrl={getTokenLogoUrl}
                    formatBalanceWithLogo={formatBalanceWithIcon}
                />
            )}
            <Toast ref={toast} />
        </div>
    );
});

export default FloatieDAO;
